import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ContextualHelpIcon from 'components/help/ContextualHelpIcon';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { RobotOutlined, CloseOutlined } from '@ant-design/icons';
import { CHAT_DRAWER_WIDTH_CSS } from 'config';
import { useChat, CHAT_PENDING_CONVERSATION_ID } from './ChatContext';
import ChatMessageList from './ChatMessageList';
import ChatInput from './ChatInput';
import ConversationList from './ConversationList';

const paperSx = {
  position: 'relative',
  width: CHAT_DRAWER_WIDTH_CSS,
  maxWidth: '100%',
  boxSizing: 'border-box',
  height: 'calc(100vh - 64px)',
  display: 'flex',
  flexDirection: 'column',
  borderLeft: '1px solid',
  borderColor: 'divider'
};

export default function ChatDrawer({ overlay = false }) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const {
    open,
    toggleDrawer,
    conversations,
    loadConversations,
    activeConversationId,
    selectConversation,
    setActiveConversationId,
    messages,
    appendMessage,
    updateLastAssistantMessage,
    updateLastAssistantReasoning,
    updateMessageById,
    upsertConversationInList,
    adoptPendingConversation,
    registerSendMessageHandler
  } = useChat();

  const [streaming, setStreaming] = useState(false);
  const [scrollToBottomOnSend, setScrollToBottomOnSend] = useState(0);
  const [tab, setTab] = useState(0);
  const abortRef = useRef(null);
  const conversationIdRef = useRef(activeConversationId);

  useEffect(() => {
    conversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    if (open) loadConversations();
  }, [open, loadConversations]);

  const handleActionResolved = (proposalId, status) => {
    if (!activeConversationId) return;
    const msg = messages[activeConversationId]?.find((m) => m.id === proposalId);
    if (!msg?.content) return;
    try {
      const proposal = JSON.parse(msg.content);
      proposal.status = status;
      updateMessageById(activeConversationId, proposalId, { content: JSON.stringify(proposal) });
      if (status === 'confirmed') {
        appendMessage(activeConversationId, {
          id: `action-done-${proposalId}`,
          role: 'assistant',
          content: t('chat.actionConfirmed'),
          createdAt: new Date().toISOString()
        });
      }
    } catch {
      /* ignore */
    }
  };

  const activeMessages = activeConversationId ? (messages[activeConversationId] ?? []) : [];

  const processEvent = (data, { convId, text, tempUserId, tempAssistantId, state }) => {
    try {
      const payload = JSON.parse(data);

      if (payload.type === 'conversation_id') {
        state.resolvedConvId = payload.conversationId;
        conversationIdRef.current = state.resolvedConvId;
        if (!convId) {
          adoptPendingConversation(state.resolvedConvId);
          setActiveConversationId(state.resolvedConvId);
          if (!state.assistantMsgAdded) {
            appendMessage(state.resolvedConvId, {
              id: tempAssistantId,
              role: 'assistant',
              content: '',
              createdAt: new Date().toISOString()
            });
            state.assistantMsgAdded = true;
          }
        }
      }

      if (payload.type === 'reasoning') {
        state.reasoningContent += payload.content ?? '';
        if (!state.assistantMsgAdded && state.resolvedConvId) {
          appendMessage(state.resolvedConvId, { id: tempAssistantId, role: 'assistant', content: '', createdAt: new Date().toISOString() });
          state.assistantMsgAdded = true;
        }
        if (state.resolvedConvId) updateLastAssistantReasoning(state.resolvedConvId, state.reasoningContent);
      }

      if (payload.type === 'tool_call' && state.resolvedConvId) {
        if (!state.assistantMsgAdded) {
          appendMessage(state.resolvedConvId, { id: tempAssistantId, role: 'assistant', content: '', createdAt: new Date().toISOString() });
          state.assistantMsgAdded = true;
        }
        if (state.reasoningContent.trim()) {
          updateLastAssistantReasoning(state.resolvedConvId, state.reasoningContent);
        }
      }

      if (payload.type === 'token') {
        state.assistantContent += payload.content ?? '';
        if (!state.assistantMsgAdded && state.resolvedConvId) {
          appendMessage(state.resolvedConvId, { id: tempAssistantId, role: 'assistant', content: '', createdAt: new Date().toISOString() });
          state.assistantMsgAdded = true;
        }
        if (state.resolvedConvId) updateLastAssistantMessage(state.resolvedConvId, state.assistantContent);
      }

      if (payload.type === 'action_proposal' && state.resolvedConvId) {
        const proposal = {
          id: payload.proposalId,
          conversationId: state.resolvedConvId,
          toolName: payload.tool,
          toolInput: payload.input ?? {},
          summaryHuman: payload.summaryHuman ?? '',
          status: 'pending',
          createdAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString()
        };
        appendMessage(state.resolvedConvId, {
          id: payload.proposalId,
          role: 'action_proposal',
          content: JSON.stringify(proposal),
          createdAt: new Date().toISOString()
        });
      }

      if (payload.type === 'content_final' && state.resolvedConvId && typeof payload.content === 'string') {
        state.assistantContent = payload.content;
        if (!state.assistantMsgAdded) {
          appendMessage(state.resolvedConvId, {
            id: tempAssistantId,
            role: 'assistant',
            content: payload.content,
            createdAt: new Date().toISOString()
          });
          state.assistantMsgAdded = true;
        } else {
          updateLastAssistantMessage(state.resolvedConvId, payload.content);
        }
      }

      if (payload.type === 'done' && state.resolvedConvId) {
        upsertConversationInList({ id: state.resolvedConvId, title: text.slice(0, 60), updatedAt: new Date().toISOString() });
        loadConversations();
      }

      if (payload.type === 'error') {
        if (payload.code === 'INSUFFICIENT_CREDITS') {
          window.dispatchEvent(new CustomEvent('insufficient-credits'));
        }
        const errorId = `err-${Date.now()}`;
        const target = state.resolvedConvId || convId || '__ephemeral__';
        appendMessage(target, { id: errorId, role: 'assistant', content: `⚠️ ${payload.message}`, createdAt: new Date().toISOString() });
        state.hadError = true;
      }
    } catch {
      /* ignore */
    }
  };

  const handleSend = async (text, explicitConvId = null) => {
    setTab(0);
    setScrollToBottomOnSend((n) => n + 1);
    setStreaming(true);
    const tempUserId = `u-${Date.now()}`;
    const tempAssistantId = `a-${Date.now()}`;
    if (explicitConvId) {
      conversationIdRef.current = explicitConvId;
      setActiveConversationId(explicitConvId);
    }
    const convId = explicitConvId || conversationIdRef.current;
    const state = { resolvedConvId: convId, assistantContent: '', reasoningContent: '', assistantMsgAdded: false, hadError: false };

    const now = new Date().toISOString();
    if (!convId) {
      setActiveConversationId(CHAT_PENDING_CONVERSATION_ID);
      appendMessage(CHAT_PENDING_CONVERSATION_ID, { id: tempUserId, role: 'user', content: text, createdAt: now });
      appendMessage(CHAT_PENDING_CONVERSATION_ID, { id: tempAssistantId, role: 'assistant', content: '', createdAt: now });
      state.assistantMsgAdded = true;
    } else {
      appendMessage(convId, { id: tempUserId, role: 'user', content: text, createdAt: now });
    }

    const token = localStorage.getItem('accessToken');
    const controller = new AbortController();
    abortRef.current = controller;
    const ctx = { convId, text, tempUserId, tempAssistantId, state };

    try {
      const response = await fetch('/api/v1/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ message: text, conversationId: convId, language: i18n.language, route: pathname }),
        signal: controller.signal
      });

      if (response.status === 403) {
        const body = await response.json().catch(() => ({}));
        if (body.code === 'INSUFFICIENT_CREDITS') {
          window.dispatchEvent(new CustomEvent('insufficient-credits'));
          const target = convId || CHAT_PENDING_CONVERSATION_ID;
          appendMessage(target, {
            id: `err-${Date.now()}`,
            role: 'assistant',
            content: `⚠️ ${body.message ?? t('credits.insufficientCredits')}`,
            createdAt: new Date().toISOString()
          });
          state.hadError = true;
          return;
        }
      }

      if (!response.ok || !response.body) throw new Error(`Request failed: ${response.status}`);

      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += value;
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            processEvent(line.slice(6), ctx);
          }
        }
      }
      if (buffer.startsWith('data: ')) {
        processEvent(buffer.slice(6), ctx);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        const target = state.resolvedConvId || convId || CHAT_PENDING_CONVERSATION_ID;
        appendMessage(target, {
          id: `err-${Date.now()}`,
          role: 'assistant',
          content: `⚠️ ${err instanceof Error ? err.message : t('chat.connectionError')}`,
          createdAt: new Date().toISOString()
        });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
      if (state.hadError && !convId) setActiveConversationId(null);
    }
  };

  useEffect(() => {
    registerSendMessageHandler(handleSend);
    return () => registerSendMessageHandler(null);
  });

  const handleSelectConversation = (id) => {
    selectConversation(id);
    setTab(0);
  };

  const drawerBody = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <RobotOutlined style={{ marginRight: 8 }} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {t('chat.title')}
        </Typography>
        <IconButton size="small" onClick={toggleDrawer}>
          <CloseOutlined />
        </IconButton>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab
          label={
            <Stack direction="row" alignItems="center" component="span">
              Chat
              <ContextualHelpIcon contextKey="chat.assistant" />
            </Stack>
          }
          sx={{ minHeight: 36, py: 0.5, fontSize: 12 }}
        />
        <Tab label={`${t('chat.history')} (${conversations.length})`} sx={{ minHeight: 36, py: 0.5, fontSize: 12 }} />
      </Tabs>

      {tab === 0 ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ChatMessageList
            messages={activeMessages}
            loading={false}
            conversationId={activeConversationId}
            scrollToBottomOnSend={scrollToBottomOnSend}
            onActionResolved={handleActionResolved}
          />
          <ChatInput onSend={handleSend} disabled={streaming} />
        </Box>
      ) : (
        <ConversationList onSelect={handleSelectConversation} />
      )}
    </>
  );

  if (overlay) {
    return (
      <>
        <Tooltip title={t('chat.title')} placement="left">
          <Fab
            color="primary"
            size="medium"
            onClick={toggleDrawer}
            sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300, display: open ? 'none' : 'flex' }}
          >
            <RobotOutlined />
          </Fab>
        </Tooltip>
        <Drawer anchor="right" open={open} variant="temporary" onClose={toggleDrawer} ModalProps={{ keepMounted: true }}>
          <Box sx={{ ...paperSx, height: '100vh', top: 0 }}>{drawerBody}</Box>
        </Drawer>
      </>
    );
  }

  return (
    <>
      <Tooltip title={t('chat.title')} placement="left">
        <Fab color="primary" size="medium" onClick={toggleDrawer} sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
          <RobotOutlined />
        </Fab>
      </Tooltip>

      <Box
        sx={{
          flexShrink: 0,
          width: open ? CHAT_DRAWER_WIDTH_CSS : 0,
          overflow: 'hidden',
          transition: 'width 225ms cubic-bezier(0, 0, 0.2, 1)'
        }}
      >
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            width: CHAT_DRAWER_WIDTH_CSS,
            flexShrink: 0,
            '& .MuiDrawer-paper': paperSx
          }}
        >
          {drawerBody}
        </Drawer>
      </Box>
    </>
  );
}
